import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgMaterialUse } from "@/lib/manufacturing/types";
import { ManufacturingEntityBulkBar } from "./ManufacturingEntityBulkBar";
import { ManufacturingMaterialUsesListToolbar } from "./ManufacturingMaterialUsesListToolbar";
import { ManufacturingModelsRecipeListPagination } from "./ManufacturingModelsRecipeListPagination";
import type { useManufacturingMaterialUsesListView } from "./useManufacturingMaterialUsesListView";
import type { useEntityBulkSelection } from "./useEntityBulkSelection";

type ListView = ReturnType<typeof useManufacturingMaterialUsesListView>;
type Bulk = ReturnType<typeof useEntityBulkSelection<MfgMaterialUse>>;

export function ManufacturingMaterialUsesListCard({
  enabled,
  saveBusy,
  listBusy,
  uses,
  selectedId,
  creating,
  listView,
  bulk,
  exportBusy,
  exportError,
  onBeginCreate,
  onBulkImport,
  onReload,
  onPickUse,
  onExportSelected,
  onExportAllFiltered,
}: {
  enabled: boolean;
  saveBusy: boolean;
  listBusy: boolean;
  uses: readonly MfgMaterialUse[];
  selectedId: string | null;
  creating: boolean;
  listView: ListView;
  bulk: Bulk;
  exportBusy: boolean;
  exportError: string | null;
  onBeginCreate: () => void;
  onBulkImport: () => void;
  onReload: () => void;
  onPickUse: (id: string) => void;
  onExportSelected: () => void;
  onExportAllFiltered: () => void;
}) {
  const paginationProps = {
    page: listView.page,
    pageCount: listView.pageCount,
    pageSize: listView.pageSize,
    rangeStart: listView.rangeStart,
    rangeEnd: listView.rangeEnd,
    total: listView.filteredCount,
    canPrev: listView.page > 0,
    canNext: listView.page + 1 < listView.pageCount,
    onSetPage: listView.setPage,
    onSetPageSize: listView.setPageSize,
  };

  return (
    <SectionCard
      title="Part uses"
      description="Jobs that several plant SKUs can fill. Prefer creating from Inventory (select 2+ materials → Make interchangeable). This slide is for editing members and promoting BOM lines."
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          onClick={onBeginCreate}
          disabled={!enabled || saveBusy}
        >
          + New use
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={onBulkImport}
          disabled={!enabled}
        >
          Bulk import
        </button>
        <button type="button" className={ghostBtnClass} onClick={onReload} disabled={listBusy}>
          {listBusy ? "Loading…" : "Reload list"}
        </button>
      </div>
      {uses.length > 0 ? (
        <ManufacturingMaterialUsesListToolbar
          search={listView.search}
          onSearchChange={listView.setSearch}
          activeFilter={listView.activeFilter}
          onActiveFilterChange={listView.setActiveFilter}
          channelFilter={listView.channelFilter}
          onChannelFilterChange={listView.setChannelFilter}
          channels={listView.channels}
        />
      ) : null}
      <ManufacturingEntityBulkBar
        entityLabel="part use"
        selectedCount={bulk.selectedCount}
        filteredCount={listView.filtered.length}
        allFilteredSelected={bulk.allFilteredSelected}
        busy={exportBusy}
        onSelectAllFiltered={bulk.selectAllFiltered}
        onClear={bulk.clearSelection}
        onExportCsv={onExportSelected}
        onExportAllFiltered={onExportAllFiltered}
        hint="Export includes use header + member materials (round-trips Bulk import)."
      />
      {exportError ? (
        <p className="mb-2 text-xs text-red-700 dark:text-red-300">{exportError}</p>
      ) : null}
      {listBusy && uses.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading uses…</p>
      ) : uses.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No uses yet — start from the guide above, or from Inventory.
        </p>
      ) : listView.filtered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No uses match your filters.</p>
      ) : (
        <>
          <ManufacturingModelsRecipeListPagination
            {...paginationProps}
            alwaysShow={listView.pageSize !== 10}
            variant="top"
          />
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {listView.pageItems.map((use) => {
              const active = use.id === selectedId && !creating;
              return (
                <li key={use.id} className="flex items-start gap-2 px-1 py-1">
                  <input
                    type="checkbox"
                    className="mt-2.5"
                    checked={bulk.selectedIds.has(use.id)}
                    onChange={() => bulk.toggleSelected(use.id)}
                    aria-label={`Select ${use.use_code}`}
                  />
                  <button
                    type="button"
                    className={
                      "flex min-w-0 flex-1 items-start justify-between gap-2 px-2 py-2 text-left text-sm " +
                      (active
                        ? "bg-indigo-50 dark:bg-indigo-950/30"
                        : "hover:bg-gray-50 dark:hover:bg-zinc-800/50")
                    }
                    onClick={() => onPickUse(use.id)}
                  >
                    <span>
                      <span className="font-semibold text-gray-900 dark:text-zinc-100">
                        {use.name}
                      </span>
                      <span className="ml-2 font-mono text-xs text-gray-500 dark:text-zinc-400">
                        {use.use_code}
                      </span>
                      {use.finish_channel ? (
                        <span className="ml-2 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-800 dark:bg-teal-950/40 dark:text-teal-200">
                          {use.finish_channel}
                        </span>
                      ) : null}
                      {!use.is_active ? (
                        <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                          inactive
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-zinc-400">{use.unit}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <ManufacturingModelsRecipeListPagination
            {...paginationProps}
            alwaysShow={listView.pageSize !== 10}
            variant="bottom"
          />
        </>
      )}
    </SectionCard>
  );
}
